import React from 'react';
import { Classable, HasChildren } from '@shapeable/types';
import styled, { css } from 'styled-components';
import { breakpoints, theme } from '@shapeable/theme';
import { WARM_GREY, PageContextDefinition, useAppState, usePageEntity, PageEntityProvider, useModal } from '@shapeable/ui';
import { LayoutShell, SiteFooterLayout, UiConfig, UiProvider } from '@shapeable/ui';
import * as pageLayouts from './page-layouts';
import * as sliceLayouts from './slice-layouts';
import { LANG_STRINGS } from '../lang';
import { LIGHT_BIEGE, LIGHT_BROWN, LayoutStyles, VERY_LIGHT_BROWN, VILLARS_FUCHSIA, VILLARS_PRIMARY_BLUE, VILLARS_PURPLE, VILLARS_TURQUOISE, YELLOW, YELLOW_DARK, themeOverrides } from '../theme';
import { MenusProvider } from '../gatsby/providers/menus-provider';
import { ContactUsIframeEmbed } from './elements/contact-us-iframe-embed';


export type LayoutPropTypes = Classable & HasChildren & {
  path?: string;
  data?: any;
  pageContext?: PageContextDefinition;
};

const LayoutDefaultProps: LayoutPropTypes = {
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    ${LayoutStyles};
  `,
});

const FooterStyles = breakpoints({
  base: css`
  `,
});

const BodyStyles = breakpoints({
  base: css`
    flex-grow: 1;
  `,
});

// -------- Components -------->

const My = {
  Container: styled(LayoutShell)`${ContainerStyles}`,
  Body: styled.div`${BodyStyles}`,
  Footer: styled(SiteFooterLayout)`${FooterStyles}`,
};

export const Layout: React.FC<LayoutPropTypes> = (props) => {
  const { className, children } = props;

  const modal = useModal();

  const onHeaderButtonClick = (slug: string) => {
    if (slug === 'contact-us-iframe-button') {
      modal?.show(<ContactUsIframeEmbed />, { color: '#000000', title: 'Contact Us', opacity: 0.5 });
    }
  }

  const uiConfig: UiConfig = {
    layouts: { pageLayouts, sliceLayouts, defaultPageLayout: pageLayouts.PageLayoutDefault },
    langStrings: LANG_STRINGS,
    theme: themeOverrides,
    header: {
      variant: 'overlay',
      showThemeMode: true,
      onHeaderButtonClick,
    },
    entity: {
      dataProviders: [
        MenusProvider,
      ]
    },
    componentProps: {
      PersonBioCell: {
        enableAutoCapitalisation: false,
      },
      SiteMenuBar: {
        themeMode: 'dark',
        colors: {
          background: VILLARS_PRIMARY_BLUE + 'FA',
          text: 'canvas',
          dark: {
            background: '#1E1E1EFA',
          }
        }
      },
      SlideControls: {
        arrowColors: {
          fill: 'light',
          hover: {
            fill: VILLARS_TURQUOISE,
          }
        },
        colors: {
          background: `${VILLARS_TURQUOISE}BF`,
        }
      },
      HeaderDivider: {
        colors: {
          border: LIGHT_BROWN,
          text: LIGHT_BROWN,
          dark: {
            border: 'light',
            text: 'light',
          }
        }
      },
      MenuItem: {
        colors: {
          bold: VILLARS_PRIMARY_BLUE,
          hover: {
            text: 'strong',
            bold: 'strong',
            background: VILLARS_PRIMARY_BLUE,
          },
          active: {
            text: VILLARS_PRIMARY_BLUE,
          },
        }
      },
      MenuItemLink: {
        colors: {
          text: 'strong',
          hover: {
            text: LIGHT_BROWN,
          },
        }
      },
      SiteFooterLayout: {
        colors: {
          background: VERY_LIGHT_BROWN,
          dark: {
            background: '#1E1E1E',
          }
        }
      },
    }
  }

  return (
    <UiProvider value={uiConfig}>
      <My.Container
        className={className}
        includeTooltip
        tooltipProps={{
          backgroundColor: '#FFFFFF',
        }}
      >
        <My.Body>
          {children}
        </My.Body>
        <My.Footer backgroundColor={VERY_LIGHT_BROWN} />
      </My.Container>
    </UiProvider>
  );
  
};

Layout.defaultProps = LayoutDefaultProps;

